import React, { useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import { CustomButton, colors } from "@ippon/shared-components";

interface Props {
  /** ファイルが選択されたら親へ通知 (実 OCR は親側で発火) */
  onFileSelected: (file: File) => void;
  /** 「手動で入力する」ボタン押下 */
  onSkipToManual: () => void;
}

const ALLOWED = ["application/pdf", "image/png", "image/jpeg", "image/jpg"];

/**
 * 支出 OCR モーダルの Upload step。
 * - ドラッグ&ドロップ / クリック選択 でファイルを受け付ける
 * - 「手動で入力する」で OCR をスキップして既存フォームへ
 */
const ExpenseOcrUploadStep: React.FC<Props> = ({
  onFileSelected,
  onSkipToManual,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File | null) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setError(
        "対応していないファイル形式です。PDF / PNG / JPEG のみ受け付けます",
      );
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("ファイルサイズが上限 (10MB) を超えています");
      return;
    }
    setError(null);
    onFileSelected(file);
  };

  return (
    <Box>
      <Box>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.text.primary,
            mt: 2,
            mb: 1,
          }}
        >
          請求書や領収書をアップロードすると、AI が自動で内容を読み取ります。
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: colors.text.secondary,
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          ※ 1 ファイルにつき 1 件のみ抽出します。複数含まれる場合は主要な 1 件が対象です。
        </Typography>

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            handleFile(e.dataTransfer.files?.[0] || null);
          }}
          onClick={() => inputRef.current?.click()}
          sx={{
            border: "1px dashed",
            borderColor: isDragging
              ? colors.primary.main
              : colors.border.light,
            backgroundColor: isDragging
              ? colors.background.selected
              : colors.background.default,
            borderRadius: "10px",
            p: 4,
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: colors.primary.main,
              backgroundColor: colors.background.selected,
            },
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,image/*"
            style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
          />
          <Stack alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: colors.background.selected,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CloudUploadIcon
                sx={{ fontSize: 28, color: colors.primary.main }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: colors.text.primary,
                }}
              >
                ファイルをドラッグ & ドロップ
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.text.secondary,
                  mt: 0.5,
                }}
              >
                または クリックしてファイルを選択
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: colors.text.disabled }}>
              対応形式: PDF, PNG, JPEG (最大 10MB)
            </Typography>
          </Stack>
        </Box>

        {error && (
          <Typography
            sx={{
              fontSize: 12,
              color: colors.status.error,
              mt: 1.5,
            }}
          >
            {error}
          </Typography>
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mt: 1, mb: 1 }}
        >
          <Box
            sx={{ flex: 1, height: "1px", backgroundColor: colors.border.light }}
          />
          <Typography sx={{ fontSize: 12, color: colors.text.secondary }}>
            または
          </Typography>
          <Box
            sx={{ flex: 1, height: "1px", backgroundColor: colors.border.light }}
          />
        </Stack>

        <Stack direction="row" justifyContent="center">
          <CustomButton
            label="手動で入力する"
            buttonCategory="submit"
            startIcon={<DescriptionIcon sx={{ fontSize: 16 }} />}
            onClick={onSkipToManual}
          />
        </Stack>
      </Box>
    </Box>
  );
};

export default ExpenseOcrUploadStep;
